import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Loader2 } from "lucide-react";

const CAUSE_COLORS = {
  Electrical: "#7C1D3A",
  Mechanical: "#B45309",
  "Hydraulic/Pneumatic": "#0F766E",
  "Operator Error": "#8B5CF6",
  "Wear-and-Tear": "#DC2626",
  Consumable: "#059669",
  "External Power": "#0369A1",
  "Software/Control": "#7C3AED",
  Other: "#64748B",
  Uncategorised: "#9CA3AF",
};

export default function BreakdownPareto() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/maintenance/breakdown-pareto")
      .then(({ data }) => {
        setRows(data.rows || []);
        setTotal(data.total_breakdowns || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <Card data-testid="breakdown-pareto-card">
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#7C1D3A]" /> Breakdown Pareto — by Root Cause
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Ranks breakdown categories by frequency. Focus on the top 2–3 causes to eliminate ~80% of downtime.
        </p>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {loading ? (
          <div className="text-slate-400 text-center py-6 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-slate-400 text-center py-8 text-sm border border-dashed border-slate-200 rounded-md">
            No breakdowns recorded yet. Once technicians tag a root cause on breakdown logs, this chart populates.
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500">
              Total breakdowns: <b className="text-slate-800">{total}</b>
            </div>
            <div className="space-y-2">
              {rows.map((r) => {
                const width = (r.count / max) * 100;
                const color = CAUSE_COLORS[r.code] || CAUSE_COLORS.Other;
                return (
                  <div key={r.code} className="text-xs" data-testid={`pareto-row-${r.code.replace(/[ /]/g, '-')}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-700">{r.code}</span>
                      <span className="font-mono text-slate-500">
                        {r.count} · {r.downtime_hours} hr · <b className="text-slate-800">{r.cumulative_pct}%</b>
                      </span>
                    </div>
                    <div className="h-4 bg-slate-100 rounded overflow-hidden relative">
                      <div className="h-full rounded" style={{ width: `${width}%`, background: color }} />
                      {/* cumulative % marker */}
                      <div
                        className="absolute top-0 h-full border-l-2 border-slate-700/40"
                        style={{ left: `${r.cumulative_pct}%` }}
                        title={`Cumulative ${r.cumulative_pct}%`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 flex-wrap text-[10px] text-slate-500 pt-2 border-t border-slate-100">
              <Badge variant="outline" className="bg-white">Bar length = frequency</Badge>
              <Badge variant="outline" className="bg-white">Vertical line = cumulative %</Badge>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
